import { call, put, select } from 'redux-saga/effects';
import {
  uploadExcelTransactionsFailure,
  uploadExcelTransactionsSuccess,
  fetchUploadedFileTransactionsSuccess,
  fetchExcelFilesMetadataRequest,
  fetchAllExcelTransactionsRequest,
} from '../actions';
import {
  uploadExcelTransactions,
  getUploadedFileTransactions,
} from '../api';
import { getUploadTransactionsTemp } from '../selectors';
import { UploadExcelTransactionsRequestAction } from '../types';
import { turnMessageOn } from 'store/app/actions';
import { logoutRequest } from 'store/auth/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { renderErrorMessage } from 'utils/render-error-message';
import { UploadExcelTransactionsResponse } from '../api';
import { fetchChartOfAccountsForOrganisationRequest } from 'store/chart-of-accounts/actions';
import { saveTaxRatesSuccess } from 'store/tax-rates/actions';
import { updateOrganisationTaxRates } from 'store/organisation/actions';
import { getTaxRates, GetTaxRatesResponse } from 'store/returns/api';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { fetchViesContactsRequest } from 'store/vat-checker/actions';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import i18n from 'i18next';
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* uploadExcelTransactionsSaga(
  action: UploadExcelTransactionsRequestAction
) {
  try {
    const { organisationId, forceDuplicates, mapping } = action.payload;
    const uploadTemp = yield select(getUploadTransactionsTemp);
    const file = uploadTemp?.file;
    let err: ApiError | null;
    //if (!file) {
    //  yield put(
    //    uploadExcelTransactionsFailure({
    //      validationErrors: [{ message: 'No file selected for upload.' }],
    //      hasDuplicates: false,
    //    })
    //  );
    //  return;
    //}

    const response: UploadExcelTransactionsResponse = yield call(
      uploadExcelTransactions,
      organisationId,
      file,
      forceDuplicates,
      mapping
    );

    err = checkResponse(response?.responseMessage);
    if (err) {
      if (
        response.data &&
        response.data.validationErrors &&
        response.data.validationErrors.length > 0
      ) {
        yield put(
          uploadExcelTransactionsFailure({
            validationErrors: response.data.validationErrors,
            hasDuplicates: response.data.hasDuplicates,
          })
        );
      } else {
        yield put(uploadExcelTransactionsFailure(null));
      }
    } else {
      const importId = response.data?.importId;
      if (!importId) {
        yield put(uploadExcelTransactionsFailure(null));
        return;
      }
      yield put(uploadExcelTransactionsSuccess(importId, organisationId));

      // Fetch first page of successful transactions (1-based; API converts to 0-based)
      const pageSize = 10;
      const transactionsResponse = yield call(
        getUploadedFileTransactions,
        organisationId,
        importId,
        1,
        pageSize
      );
      const files = transactionsResponse?.data?.files;
      const content = files?.content ?? [];
      const total = files?.totalElements ?? 0;
      const transactions = content.map((row: { errorReason?: string }) => ({
        ...row,
        errorReason: '',
      }));
      yield put(
        fetchUploadedFileTransactionsSuccess(
          transactions,
          total,
          1,
          files?.pageSize ?? pageSize
        )
      );

      const message: MessageData = {
        title: i18n.t('common.success'),
        description: i18n.t('common.excel-transactions-uploaded-successfully'),
        type: MessageStates.SUCCESS,
      };
      yield put(turnMessageOn(message));

      // Refresh Excel files metadata so the new upload appears in the list
      yield put(fetchExcelFilesMetadataRequest(organisationId));

      // Refresh all-transactions page so the Transactions tab becomes enabled
      // (hasNoTransactions depends on allExcelTransactionsPage.total)
      yield put(fetchAllExcelTransactionsRequest(organisationId, 1, pageSize));

      // Refresh chart of accounts for this organisation so Account Name dropdown
      // reflects the latest COA data after the new upload.
      yield put(fetchChartOfAccountsForOrganisationRequest(organisationId));

      // Refresh tax rates only for this organisation's connection, following the
      // same pattern as the Tax Rates tab (updateExcelTaxRateNameSaga).
      const organisations = yield select(getOrgOrganisations);
      const org =
        organisations &&
        (organisations as any[]).find((o) => o?.uuid === organisationId);
      const connectionId: string | undefined = org?.connectionId;
      if (connectionId) {
        const taxRatesResponse: GetTaxRatesResponse = yield call(
          getTaxRates,
          connectionId
        );
        const inner: any =
          (taxRatesResponse as any)?.data ?? (taxRatesResponse as any);
        const freshTaxRates = inner?.taxRates ?? [];
        const freshTaxEnabled = inner?.taxEnabled ?? false;

        yield put(saveTaxRatesSuccess(freshTaxRates, connectionId));
        yield put(
          updateOrganisationTaxRates(
            connectionId,
            freshTaxRates,
            freshTaxEnabled
          )
        );

        // Refresh VIES contacts for this connection so the Transactions tab
        // Name dropdown has up-to-date contact options after a new upload.
        // Use suppressMessages=true to avoid "no contacts" toasts on entity side.
        yield put(fetchViesContactsRequest(connectionId, true));
      }
    }
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(uploadExcelTransactionsFailure(null));
  }
}
